import { useMemo, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAlert, mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { $api } from '../api/queryClient'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { useFormatters } from '../lib/format'
import { SECTION_ICONS } from '../lib/section-icons'
import {
  localizedLabel,
  runActorLabel,
  runSourceLabel,
  SYNC_FAMILIES,
  type SyncOutcomeItem,
} from '../lib/ontology-sync'
import { PageHeader } from './shared/PageHeader'
import { FieldDiff } from './ontology/FieldDiff'
import { Alert, AlertTitle } from './ui/alert'
import { AppIcon } from './ui/icon'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { EmptyState } from './ui/empty-state'
import { Skeleton } from './ui/skeleton'

const ACTION_BADGE: Record<string, 'success' | 'warning' | 'secondary' | 'info'> = {
  created: 'success',
  updated: 'warning',
  unchanged: 'secondary',
  collateral: 'info',
}

/** One applied sync: what it changed, item by item, as measured. */
export function TypesSyncRunPage() {
  const { org, runUid } = useParams({ from: '/$org/types/sync/runs/$runUid' })
  const { t } = useTranslation('types')
  const { locale } = useLocale()
  const { formatDate } = useFormatters()

  const query = $api.useQuery('get', '/api/v1/organizations/{orgCode}/types/sync/runs/{runUid}', {
    params: { path: { orgCode: org, runUid } },
  })
  const run = query.data?.data
  const when = run ? formatDate(run.finished_at, { dateStyle: 'medium', timeStyle: 'short' }) : ''

  useBreadcrumbs([
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb.types'), path: `/${org}/types` },
    { title: t('sync.breadcrumb'), path: `/${org}/types/sync` },
    { title: t('sync.history'), path: `/${org}/types/sync/runs` },
    { title: when || t('sync.run.breadcrumb'), path: `/${org}/types/sync/runs/${runUid}` },
  ])

  const byFamily = useMemo(() => {
    const groups = new Map<string, SyncOutcomeItem[]>()
    for (const it of run?.items ?? []) {
      const list = groups.get(it.family) ?? []
      list.push(it)
      groups.set(it.family, list)
    }
    return SYNC_FAMILIES.filter((f) => groups.has(f)).map((f) => [f, groups.get(f) ?? []] as const)
  }, [run])

  if (query.isLoading) {
    return (
      <div className="space-y-2" data-testid="ontology-sync-run-loading">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (!run) {
    return (
      <Alert variant="destructive" data-testid="ontology-sync-run-not-found">
        <AppIcon path={mdiAlert} size="md" />
        <AlertTitle>{t('sync.run.not_found')}</AlertTitle>
      </Alert>
    )
  }

  const notConverged = run.items.filter((it) => !it.converged)
  const residualTitle =
    run.source.kind === 'defaults' ? t('sync.run.residual_title_defaults') : t('sync.run.residual_title')
  const sourceSide = run.source.kind === 'defaults' ? t('sync.side_defaults') : t('sync.side_template')

  return (
    <div className="space-y-6">
      <PageHeader
        data-testid="ontology-sync-run-header"
        icon={SECTION_ICONS.types}
        title={t('sync.run.title', { date: when })}
        description={t('sync.run.description', { source: runSourceLabel(run, t), user: runActorLabel(run, t) })}
      />

      <p className="text-sm" data-testid="ontology-sync-run-counts">
        {t('sync.run.counts', {
          created: run.counts.created,
          updated: run.counts.updated,
          unchanged: run.counts.unchanged,
        })}
      </p>

      {byFamily.length === 0 && <EmptyState variant="inline" title={t('sync.run.items_empty')} />}

      {byFamily.map(([family, items]) => (
        <section key={family} className="space-y-2" data-testid={`ontology-sync-run-family-${family}`}>
          <h2 className="text-base font-semibold">
            {t(`sync.families.${family}`)} <span className="text-sm font-normal text-muted-foreground">({items.length})</span>
          </h2>
          <ul className="divide-y rounded-lg border">
            {items.map((it) => (
              <RunItem key={`${it.family}-${it.key}`} item={it} locale={locale} />
            ))}
          </ul>
        </section>
      ))}

      <section className="space-y-2" data-testid="ontology-sync-run-residual">
        <h2 className="text-base font-semibold">{residualTitle}</h2>
        {notConverged.length === 0 ? (
          <EmptyState variant="inline" title={t('sync.run.residual_empty')} />
        ) : (
          <ul className="divide-y rounded-lg border">
            {notConverged.map((it) => (
              <li key={`${it.family}-${it.key}`} className="space-y-1 px-3 py-2">
                <p className="text-sm font-medium">
                  {t(`sync.families.${it.family}`)} · {localizedLabel(it.label, locale) || it.key}
                </p>
                <FieldDiff changes={it.residual ?? []} beforeLabel={t('sync.after')} afterLabel={sourceSide} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2" data-testid="ontology-sync-run-collateral">
        <h2 className="text-base font-semibold">{t('sync.run.collateral_title')}</h2>
        {run.collateral.length === 0 ? (
          <EmptyState variant="inline" title={t('sync.run.collateral_empty')} />
        ) : (
          <ul className="divide-y rounded-lg border">
            {run.collateral.map((it) => (
              <RunItem key={`${it.family}-${it.key}`} item={it} locale={locale} showFamily />
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2" data-testid="ontology-sync-run-warnings">
        <h2 className="text-base font-semibold">{t('sync.run.warnings_title')}</h2>
        {run.warnings.length === 0 ? (
          <EmptyState variant="inline" title={t('sync.run.warnings_empty')} />
        ) : (
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
            {run.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function RunItem({ item, locale, showFamily }: { item: SyncOutcomeItem; locale: string; showFamily?: boolean }) {
  const { t } = useTranslation('types')
  // An update is what a reader comes for: open. A creation lists every field
  // of the item, and a first sync creates hundreds: closed until asked.
  const [open, setOpen] = useState(item.action !== 'created')
  const label = localizedLabel(item.label, locale) || item.key
  const hasDiff = item.action !== 'unchanged' && item.changes.length > 0
  return (
    <li className="space-y-2 px-3 py-2" data-testid={`ontology-sync-run-item-${item.family}-${item.key}`} data-action={item.action}>
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {showFamily && <span className="text-xs text-muted-foreground">{t(`sync.families.${item.family}`)} ·</span>}
          <span className="text-sm font-medium">{label}</span>
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">{item.key}</code>
          <Badge variant={ACTION_BADGE[item.action] ?? 'secondary'}>{t(`sync.run.action.${item.action}`)}</Badge>
        </div>
        {hasDiff && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? t('sync.hide_changes') : t('sync.show_changes')}
            title={open ? t('sync.hide_changes') : t('sync.show_changes')}
            data-testid={`ontology-sync-run-toggle-${item.family}-${item.key}`}
          >
            <AppIcon path={open ? mdiChevronDown : mdiChevronRight} size="sm" />
          </Button>
        )}
      </div>
      {hasDiff && open && (
        <FieldDiff
          changes={item.changes}
          beforeLabel={item.action === 'created' ? undefined : t('sync.before')}
          afterLabel={t('sync.after')}
          data-testid={`ontology-sync-run-diff-${item.family}-${item.key}`}
        />
      )}
    </li>
  )
}
